import React, { FC, createContext, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { Redirect, Route, Switch } from "react-router-dom";
import useFlash from "go-alert/AlertMessage";
import Page404 from "go-app/components/Page404/Page404";
import handleError from "go-app/services/errors";
import { useBrowserTabTitle } from "go-core/components/BrowserTab/useBrowserTabTitle";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { StocktakingDraftApi } from "../../../../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../../../../services/Api/api";
import { StocktakingContext } from "../../../../Preview";
import StocktakingDraftsMenu from "../StocktakingDraftsMenu/StocktakingDraftsMenu";
import StocktakingDraftStateList from "./DraftStateList/StocktakingDraftStateList";
import StocktakingDraftSummaryList from "./StocktakingDraftSummaryList";

interface StocktakingDraftsContextType {
	drafts: StocktakingDraftApi[];
	setDrafts: (drafts: StocktakingDraftApi[]) => void;
}

export const StocktakingDraftsContext = createContext<StocktakingDraftsContextType>({
	drafts: [],
	setDrafts: (drafts: StocktakingDraftApi[]) => {},
});

const StocktakingDraftsTab: FC<RouteComponentProps> = (props) => {
	const [stocktakingDrafts, setStocktakingDrafts] = useState<StocktakingDraftApi[] | undefined>(undefined);
	const { stocktaking } = useContext(StocktakingContext);
	const { addFlash } = useFlash();
	const { t } = useTranslation();
	const { handleChangeTabTitle } = useBrowserTabTitle();
	const { width } = useWindowSize();
	const shouldGiveSubNavigationMoreSpace = width < 1200 && width > 767;

	useEffect(() => {
		const init = async () => {
			const drafts = await fetchStocktakingDrafts();
			handleChangeTabName(drafts);
		};

		init();
	}, []);

	const handleChangeTabName = (drafts?: StocktakingDraftApi[]) => {
		const draftRegex = /drafts\/\d+$/;
		const pathname = props.location.pathname;

		if (pathname.endsWith("drafts")) {
			handleChangeTabTitle(
				`${stocktaking.document_number} | ${t("modules.stocktaking.header.title")} | ${t(
					"modules.stocktaking.field.draft.title"
				)} | ${t("lib:common.word.summary")}`
			);
		} else if (pathname.match(draftRegex)) {
			const pathnameParts = pathname.split("/");
			const draftName = (drafts ?? stocktakingDrafts)?.find(
				({ id }) => id.toString() === pathnameParts[pathnameParts.length - 1]
			)?.name;
			handleChangeTabTitle(
				`${stocktaking.document_number} | ${t("modules.stocktaking.header.title")} | ${draftName || ""}`
			);
		}
	};

	useEffect(() => {
		handleChangeTabName();
	}, [props.location.pathname, stocktakingDrafts]);

	const fetchStocktakingDrafts = async () => {
		try {
			const res = await api.organization().getStocktakingDrafts(stocktaking.id);
			setStocktakingDrafts(res);
			return res;
		} catch (err) {
			handleError.alert(err, addFlash);
		}
	};

	if (!stocktakingDrafts) return <LoadingContainer />;

	const handleUpdateStocktakingDraft = (index: number, stocktakingDraft: StocktakingDraftApi) => {
		const newStocktakingDrafts = [...stocktakingDrafts];
		newStocktakingDrafts[index] = {
			...newStocktakingDrafts[index],
			name: stocktakingDraft.name,
		};
		setStocktakingDrafts([...newStocktakingDrafts]);
		handleChangeTabName();
	};

	return (
		<StocktakingDraftsContext.Provider
			value={{
				setDrafts: setStocktakingDrafts,
				drafts: stocktakingDrafts,
			}}
		>
			<div className="stocktaking-drafts-menu row no-gutters ms-n3">
				<div className={`${shouldGiveSubNavigationMoreSpace ? "col-md-3" : "col-md-2"}`}>
					<StocktakingDraftsMenu
						stocktakingDrafts={stocktakingDrafts}
						stocktaking={stocktaking}
						fetchStocktakingDrafts={fetchStocktakingDrafts}
						handleUpdateStocktakingDraft={handleUpdateStocktakingDraft}
					/>
				</div>
				<div
					className={`${shouldGiveSubNavigationMoreSpace ? "col-md-9" : "col-md-10"} stocktaking-draft-list`}
				>
					<Switch>
						<Route path={`${props.match.url}/:draftId`} component={StocktakingDraftStateList} />
						<Route path="" component={StocktakingDraftSummaryList} />
						<Redirect from={props.match.url} exact to={`${props.match.url}/drafts`} />
						<Route component={Page404} />
					</Switch>
				</div>
			</div>
		</StocktakingDraftsContext.Provider>
	);
};

export default StocktakingDraftsTab;
